export const mainConcernOptions = [
    { value: 'speech and language', text: 'Speech & Language' },
    { value: 'motor skills', text: 'Motor Skills' },
    { value: 'social skills', text: 'Social Skills' },
    { value: 'learning difficulty', text: 'Learning Difficulty' },
    { value: 'behavioral concerns', text: 'Behavioral Concerns' },
    { value: 'sensory processing', text: 'Sensory Processing' },
    { value: 'other', text: 'Other (please specify)' },
]

export const currentDiagnoseStepOptions = [
    { value: 'pre-diagnosis', text: 'No diagnoses yet, exploring concerns' },
    { value: 'pre-diagnosis-waitlist', text: 'Currently on the waitlist for an evaluation' },
    { value: 'in evaluation process', text: 'In evaluation process' },
    { value: 'diagnosed recently', text: 'Recently Diagnosed' },
    { value: 'diagnosed over a year ago', text: 'Diagnosed over a year ago' },
]

export const ageOptions = [
    { value: '0-3', text: '0-3 Years' },
    { value: '4-5', text: '4-5 Years' },
    { value: '6-12', text: '6-12 Years' },
    { value: '13-17', text: '13-17 Years' },
    { value: '18+', text: '18+ years' },
]

export const monthOptions = [
    { value: '1', text: 'January' },
    { value: '2', text: 'February' },
    { value: '3', text: 'March' },
    { value: '4', text: 'April' },
    { value: '5', text: 'May' },
    { value: '6', text: 'June' },
    { value: '7', text: 'July' },
    { value: '8', text: 'August' },
    { value: '9', text: 'September' },
    { value: '10', text: 'October' },
    { value: '11', text: 'November' },
    { value: '12', text: 'December' },
]

export const birthYearOptions: { value: string; text: string }[] = (() => {
    const currentYear = new Date().getFullYear()
    const oldestYear = currentYear - 100
    const years: { value: string; text: string }[] = []
    for (let y = currentYear; y >= oldestYear; y--) {
        years.push({ value: String(y), text: String(y) })
    }
    return years
})()

export const servicesReceivedOptions = [
    { value: 'early-intervention', text: 'Early Intervention' },
    { value: 'aba', text: 'ABA' },
    { value: 'speech', text: 'Speech' },
    { value: 'ot', text: 'OT (Occupational Therapy)' },
    { value: 'pt', text: 'PT (Physical Therapy)' },
]

export const SERVICES_CONSENT_LABEL =
    "I consent to BrightPath using information about my child's services received to personalize my Roadmap."

export function deriveAgeBucket(birthMonth?: number, birthYear?: number): string | undefined {
    if (!birthMonth || !birthYear) return undefined
    const now = new Date()
    let age = now.getFullYear() - birthYear
    if (now.getMonth() + 1 < birthMonth) age -= 1
    if (age < 0) return undefined
    if (age <= 3) return '0-3'
    if (age <= 5) return '4-5'
    if (age <= 12) return '6-12'
    if (age <= 17) return '13-17'
    return '18+'
}

export const guardianRoleOptions = [
    { value: 'parent', text: 'Parent' },
    { value: 'grandparent', text: 'Grandparent' },
    { value: 'caregiver', text: 'Caregiver' },
    { value: 'autism_support_professional', text: 'Autism Support Professional' },
    { value: 'other', text: 'Other (please specify)' },
]
export const genderOptions = [
    { value: 'male', text: 'Male' },
    { value: 'female', text: 'Female' },
    { value: 'prefer_not_to_say', text: 'Prefer not to say' },
]

export const diagnosisOptions = [
    { value: 'Autism Spectrum Disorder (ASD)', text: 'Autism Spectrum Disorder (ASD)' },
    { value: 'Developmental delay', text: 'Developmental delay' },
    { value: 'Speech or language delay', text: 'Speech or language delay' },
    { value: 'ADHD / attention concerns', text: 'ADHD / attention concerns' },
    { value: 'Sensory processing differences', text: 'Sensory processing differences' },
    { value: 'Cognitive or intellectual delays', text: 'Cognitive or intellectual delays' },
    { value: 'other', text: 'Other (Please specify)' },
]

export const userGoalsForChildOptions = [
    { value: 'improve-communication', text: 'Improve communication (speech, language, or alternative communication)' },
    { value: 'build-social-skills', text: 'Build social skills and peer relationships' },
    { value: 'reduce-challenging-behaviors', text: 'Reduce challenging behaviors (meltdowns, aggression, etc.)' },
    { value: 'support-academics', text: 'Support school readiness or academic progress' },
    { value: 'strengthen-daily-routines', text: 'Strengthen daily routines (sleep, mealtime, transitions)' },
    { value: 'coordinate-therapies', text: 'Explore and coordinate therapies/treatments (ABA, speech, OT, PT, etc.)' },
    { value: 'family-caregiver-support', text: 'Find family/caregiver support and stress management resources' },
    {
        value: 'increase-independence',
        text: 'Increase independence with life skills (self-care, chores, community participation)',
    },
    { value: 'plan-for-future', text: 'Plan for the future (transition to adulthood, jobs, independent living)' },
    { value: 'other', text: 'Other' },
]

export const userGoalsAsAParentOptions = [
    { value: 'understand-autism', text: 'Better understand autism and treatment options' },
    { value: 'navigate-systems', text: 'Learn how to navigate providers, schools, and support systems' },
    { value: 'stay-organized', text: 'Stay organized with appointments, documents, and care plans' },
    { value: 'caregiver-wellbeing', text: 'Reduce stress and find caregiver self-care strategies' },
    { value: 'connect-with-others', text: 'Connect with other parents and caregivers for support' },
    {
        value: 'improve-team-communication',
        text: 'Improve communication and collaboration with healthcare/education teams',
    },
    {
        value: 'explore-financial-resources',
        text: 'Explore financial assistance or benefits (insurance, Medicaid, grants, etc.)',
    },
    { value: 'balance-life', text: 'Balance caregiving with work, family, and personal life' },
    { value: 'build-advocacy-skills', text: 'Build advocacy skills to stay proactive and in control' },
    { value: 'other', text: 'Other' },
]

export const communicationLevelOptions = [
    { value: 'words-or-sentences', text: 'Uses words or short sentences' },
    { value: 'few-words-or-sounds', text: 'Uses a few words or sounds' },
    { value: 'non-verbal-aac', text: 'Communicates without spoken words (e.g., using pictures or AAC)' },
    { value: 'no-spoken-words', text: 'Does not use spoken words yet' },
]

export const supportNeedsOptions = [
    { value: 'full-support', text: 'Needs full support' },
    { value: 'some-support', text: 'Some routines with support' },
    { value: 'mostly-independent', text: 'Mostly independent for their age' },
    { value: 'varies', text: 'Varies day to day' },
]

export const previousActivitiesOptions = [
    { value: 'speech-therapy', text: 'Speech therapy' },
    { value: 'occupational-therapy', text: 'Occupational therapy' },
    { value: 'physical-therapy', text: 'Physical therapy' },
    { value: 'aba-therapy', text: 'Applied Behavior Analysis (ABA)' },
    { value: 'social-skills-groups', text: 'Social skills groups' },
    { value: 'medications', text: 'Medications' },
    { value: 'special-education', text: 'Special education services' },
    { value: 'dietary-interventions', text: 'Dietary interventions' },
    { value: 'sensory-interventions', text: 'Sensory interventions' },
    { value: 'family-therapy', text: 'Family therapy or counseling' },
    { value: 'none', text: 'None of the above' },
    { value: 'other', text: 'Other activities' },
]
